/**
 * AdvanceArea — "Did Primelane finance the premium, and how is it recovered?"
 *
 * FOUR FACTS AND TWO POSSIBLE DOCUMENTS [D-B45]. The premium, the rate, what the driver paid towards
 * it and the weekly recovery are the same four boxes either way — what changes is what they ADD UP
 * TO. A year the driver settled in full advances nothing, charges nothing and is papered as an
 * Insurance Premium Payment Record; anything less is an Insurance Premium Advance recovered weekly,
 * on top of the contract instalment. The shape is decided by the deposits and by whether an
 * instalment row will exist, which is `insuranceInstrument`'s two limbs and not this canvas's guess.
 *
 * AND THE ADVANCE ITSELF COMES BACK TWO WAYS [D-B57]. Where the contract's hire purchase agreement
 * leaves no room for an advance instalment, a premium financed on it is settled at the end of the
 * contract: nothing separate is added to the weekly, and once the vehicle is paid for the driver
 * keeps paying the contract instalment until the advance is recovered. So the weekly pair is an
 * option there rather than a requirement, the grid below states the one repayment row that page
 * prints, and no term is drawn, because the recovery starts on an event rather than a date and
 * cannot be counted in weeks at signing. Everywhere else the pair is required, and a recovery
 * already running may be corrected but not cleared. `snapshot.hpClass` and whether the year
 * finances anything are the facts; nothing on this canvas mentions paper, because what an admin
 * needs to know is how the advance comes back.
 *
 * THE DRIVER'S OWN PAYMENT IS A DOOR HERE AND A READ ON CONTRACT SETUP. The side sheet wrote
 * `insurance_loan_deposits` from a step of its own and the setup canvas deliberately shows it
 * without a control — a third writer onto one link row is a third place it can be created. This
 * dossier is the one that writes it, so the picker lives here, and the figure it hands back rides
 * the id because "does the driver's payment cover the premium?" is asked of the DRAFT rather than of
 * a round trip.
 *
 * WHAT THE DOCUMENT WILL PRINT IS THE SAME BLOCK THE CONFIRM DIALOG DRAWS, from the same function.
 * `insuranceSummary` composes the instrument's own rows in the instrument's own order under the
 * instrument's own labels; rendering them here rather than restating them is what stops the canvas
 * and the dialog an admin confirms on from describing two different pages. The term is this canvas's
 * one addition, because it is derived NET of the driver's payment [D-B46] and belongs beside the
 * figures it is net of.
 *
 * AND THE WEEKLY TOTAL IS THE CONSEQUENCE, STATED BEFORE IT IS COMMITTED. Typing 150 here raises
 * what the driver owes every Monday from the first recovery's week — composed from the contract's
 * own instalment and every OTHER standing advance, this policy's own loan having been taken out of
 * the sum by the selector. `ForwardWeeklyPanel` carries the whole argument; what differs on this
 * canvas is the words over its two Σ rows and the absence of a term row inside it.
 *
 * FROZEN IS READ-ONLY, NOT DISABLED-LOOKING [F7]. Once the instrument carries a signature every
 * figure it printed is a read, and the correction is void-and-reissue on the Agreement area — which
 * the lock line names, because a field that simply stopped responding would be a defect as far as
 * anybody using it is concerned.
 */

import type { ReactNode } from 'react';
import { format } from 'date-fns';

import type { SummaryClause } from '../../activationSummary';
import { formatCurrency, parseBusinessDay } from '@/lib/format';
import { INSTRUMENT_TITLES, type InsuranceInstrument } from '@/lib/agreements/instrument';

import { Block, Blocks, Hint, LockLine, Rec, Rows } from '../../areas/blocks';
import QuietField from '../../inline/QuietField';
import TransactionPicker from '../../inline/TransactionPicker';
import ForwardWeeklyPanel from '../../advance/areas/ForwardWeeklyPanel';
import {
    depositsCoverAmount,
    derivedTerm,
    draftDepositTotal,
    printedMoney,
    type WeeklyInstalmentRow,
} from '../insuranceArithmetic';
import { insuranceDraftOf, type InsuranceFormValues } from '../insuranceForm';
import {
    financesAdvance,
    isAtEndAdvance,
    writesAtEndAdvance,
    type InsuranceSnapshot,
} from '../insuranceState';
import { insuranceSummary } from '../insuranceSummary';
import { frozenLock } from './lockCopy';

interface Props {
    mode: 'create' | 'edit';
    /** The instrument is signed: every figure below is a read [F7]. */
    frozen: boolean;
    /** Which instrument this DRAFT would be published on — the lock line names it. */
    instrument: InsuranceInstrument;
    form: InsuranceFormValues;
    /**
     * Every patch this canvas makes.
     *
     * THE PAGE ROUTES IT THROUGH `applyInsuranceEdit` [D-B45]: an edit that makes the driver's
     * payment cover the whole premium drops a weekly pair somebody typed in this session, and never
     * one already saved. That rule is one function and it is not this canvas's to re-state.
     */
    onChange: (updates: Partial<InsuranceFormValues>) => void;
    /** Σ of the deposit rows this dossier does not bind — the second payment on loan 294 [D-B45]. */
    otherDeposits: number;
    /** The weekly recovery is a row in the database, not a plan typed in this session. */
    instalmentSaved: boolean;
    /** The whole year, as the rail reads it — the proof-read block's other half. */
    snapshot: InsuranceSnapshot;
    /** The forward sum's OTHER terms — this policy's loan is never among them. */
    forward: {
        contractInstalment: number | null;
        insuranceLoanInstalments: WeeklyInstalmentRow[];
        loanInstalments: WeeklyInstalmentRow[];
    };
    /** The write's own refusal for one of this area's fields, by FORM FIELD NAME. */
    errors?: Record<string, string>;
    /** The create bar, or the edit bar for this area. Drawn by the page. */
    commit?: ReactNode;
}

function money(value: number | null): string | null {
    return value === null || value === undefined ? null : formatCurrency(value);
}

/**
 * One clause of the instrument as a record row.
 *
 * THREE SHAPES, AND THE ROW HAS TO READ RIGHT IN ALL THREE. A figure with a tail is a value and a
 * sub ("GH₵1,000.00, being 43 per cent of the Amount advanced…"); a row of fixed wording carries no
 * figure at all and the wording IS the value ("Nil — Primelane advanced nothing towards this
 * premium."); and a figure nobody has typed is absent, which is what `Rec`'s own empty text is for —
 * with its tail withheld, because a sentence explaining a dash is a sentence about nothing.
 */
function clauseRow(clause: SummaryClause) {
    const missing = clause.parts.some((piece) => piece.kind === 'missing');
    const values = clause.parts
        .filter((piece) => piece.kind === 'value')
        .map((piece) => piece.text)
        .join('');
    const tail = clause.parts
        .filter((piece) => piece.kind === 'text')
        .map((piece) => piece.text)
        .join('');

    if (missing) return <Rec key={clause.no} label={clause.no} value={null} empty="—" />;
    if (!values) return <Rec key={clause.no} label={clause.no} value={tail} empty="—" />;
    return (
        <Rec key={clause.no} label={clause.no} value={values} sub={subText(tail)} empty="—" />
    );
}

/**
 * The clause's tail as a line of its own.
 *
 * `.sd-rec-v .sd-sub` IS `display: block`, so the sentence the dialog runs on after the figure
 * starts a new line here — and a line opening with a comma or a dash reads as text that lost its
 * first half. The separator is dropped and nothing else is touched: the prose stays the proof-read's
 * own, which is the point of drawing these rows from it.
 */
function subText(tail: string): string | undefined {
    const text = tail.replace(/^[\s,—]+/, '');
    return text || undefined;
}

export default function AdvanceArea({
    mode,
    frozen,
    instrument,
    form,
    onChange,
    otherDeposits,
    instalmentSaved,
    snapshot,
    forward,
    errors,
    commit,
}: Props) {
    const draft = insuranceDraftOf(form);
    const depositTotal = draftDepositTotal(form, otherDeposits);
    const paidInFull = depositsCoverAmount(form, otherDeposits);
    const isRecord = instrument === 'PAYMENT_RECORD';
    const lock = frozenLock(instrument);

    /*
     * IS THE WEEKLY PAIR AN OPTION ON THIS CONTRACT, OR A REQUIREMENT? [D-B57] — the class of the
     * HIRE PURCHASE agreement, and deliberately not the shape of the weekly boxes. The aside and
     * the hint below describe those boxes, so they must not change their minds halfway through a
     * figure somebody is typing into them.
     *
     * A YEAR THAT FINANCES NOTHING IS NOT ASKED THE QUESTION AT ALL, and that limb reads the PREMIUM
     * box rather than the weekly pair. 54 live junctions carry cover and no loan; calling the pair
     * optional on one of those would offer an arrangement over money nobody borrowed. It moves when
     * a premium is first typed, which is the moment the year starts financing something.
     */
    const weeklyOptional = snapshot.hpClass === 'PAPER' && financesAdvance(snapshot);
    /*
     * WHAT THIS DRAFT WOULD PUBLISH, which is the question the block that proof-reads the page asks:
     * the grid comes from `insuranceSummary` off the same predicate, and a term row drawn beside a
     * repayment row that states no weekly figure would be amortising a schedule the page does not
     * carry.
     */
    const atEndDraft = writesAtEndAdvance(snapshot);
    /* AND WHAT IS ON FILE, for the reads — a frozen year is the year as it was signed, not a draft. */
    const atEndFiled = isAtEndAdvance(snapshot);

    /* THE FIGURES THE DOCUMENT WILL CARRY — the rate the save will store and the resolver's own
       clamp, from the one function the confirm dialog reads. */
    const printed = printedMoney(draft.premiumAmount, depositTotal, draft.interestRate);
    const term = derivedTerm(printed.total, draft.weeklyAmount);
    const summary = insuranceSummary(draft, snapshot, depositTotal);

    /*
     * Parsed field by field, so the calendar opens on the day the column holds — and `setDate`
     * writes that same day straight back out. `insurance_loan_instalments.starts_at` is a `date`
     * column, so the string discipline is the whole of it: `new Date('2026-08-01')` is UTC midnight
     * read back with LOCAL getters, which walks the day back at any negative offset, and compounds.
     */
    const startsOn = parseBusinessDay(form.firstStartsAt);

    return (
        <Blocks>
            <Block id="b-advance" title="The premium" aside="required">
                {frozen && <LockLine title={lock.title}>{lock.body}</LockLine>}

                <p className="sd-lede sd-dim">
                    What the year of cover cost, and what Primelane charges for financing it. The
                    premium is what the insurer was paid; the amount advanced is that figure less
                    whatever the driver paid towards it, and the interest is charged on the
                    difference alone.
                </p>

                {frozen ? (
                    <Rows>
                        <Rec label="Premium" value={money(draft.premiumAmount)} empty="Not set" />
                        <Rec
                            label="Interest rate"
                            value={draft.interestRate === null ? null : `${draft.interestRate}%`}
                            empty="Not set"
                        />
                    </Rows>
                ) : (
                    <Rows>
                        {/* "PREMIUM" AND NOT "AMOUNT FINANCED" [D-B46]. The column behind this field
                            is `insurance_loans.amount`, which is what the year of cover COST — and
                            since the driver's own payment comes off the advance, the two figures part
                            company on every part-paid premium. A field labelled for the advance while
                            holding the premium is the one place an admin would type the net figure
                            and halve the advance twice over. */}
                        <QuietField
                            id="f-loanAmount"
                            label="Premium"
                            value={form.premiumAmount}
                            onValueChange={(value) => onChange({ premiumAmount: value })}
                            inputMode="decimal"
                            placeholder="—"
                            format="GHS · the advance is this less what the driver paid"
                            error={errors?.premiumAmount}
                        />
                        {/* ZERO IS A FIGURE AND AN EMPTY BOX IS AN ABSENCE. `interest_rate` is
                            nullable, so a cleared box writes NULL and a typed 0 writes 0 — both
                            print as 0 per cent on a page that charges nothing, which is why the row
                            below states what will be STORED rather than what is in the box. */}
                        <QuietField
                            id="f-loanRate"
                            label="Interest rate"
                            value={form.interestRate}
                            onValueChange={(value) => onChange({ interestRate: value })}
                            inputMode="decimal"
                            placeholder="0"
                            format="% · empty means interest free"
                            error={errors?.interestRate}
                        />
                    </Rows>
                )}

                {/*
                 * THE TWO LIMBS ARE NOT THE SAME QUESTION, AND THIS IS WHERE THEY PART COMPANY
                 * [D-B45]. Payments covering the premium zero the stored rate on their own;
                 * `agreement_create` still publishes an ADVANCE wherever an instalment row exists,
                 * whatever the deposits say. So a canvas that announced the Payment Record off the
                 * coverage alone would name the wrong document over exactly the contradiction the
                 * weekly block below asks the admin to resolve.
                 */}
                {paidInFull && (
                    <Hint className="mt-3">
                        The driver’s payments cover the whole premium, so nothing is advanced and no
                        interest arises — the rate is stored as nought whatever is typed above.{' '}
                        {isRecord
                            ? 'This year is papered as an Insurance Premium Payment Record.'
                            : 'A weekly recovery is still standing below, so the year stays on an Insurance Premium Advance — clear both of those boxes if there is nothing to recover.'}
                    </Hint>
                )}
            </Block>

            {/* THE LINE IS DRAWN ON EVERY BLOCK THAT TURNED TO READS, which is the advance
                dossier's own arrangement (its two canvases each draw their own). A block whose
                fields simply stopped being fields, with the explanation two blocks further up, is a
                block that reads as broken to whoever scrolled straight to it. */}
            <Block id="b-deposit" title="Paid by the driver" aside="records, not requirements">
                {frozen && <LockLine title={lock.title}>{lock.body}</LockLine>}

                <p className="sd-lede sd-dim">
                    If the driver paid towards this premium, link the payment here. A part payment
                    reduces the amount advanced and the interest with it; a payment that covers the
                    whole premium means Primelane advanced nothing.
                </p>

                {frozen ? (
                    <Rows>
                        <Rec
                            label="Paid by the driver"
                            value={depositTotal === 0 ? null : money(depositTotal)}
                            empty="Nothing paid"
                        />
                    </Rows>
                ) : (
                    <Rows>
                        <TransactionPicker
                            id="f-deposit"
                            label="Payment from the driver"
                            type="CREDIT"
                            value={form.depositTransactionId}
                            /* THE AMOUNT TRAVELS WITH THE ID, AND THE SIGN STAYS ON IT. Which
                               instrument this year is published on turns on this figure, so it is
                               the picked row's own amount and never a read of the server's copy a
                               round trip behind the click — and it is stored exactly as the bank
                               recorded it, because the server judges the same question on a raw
                               `SUM(t.amount)` over these rows (20260902010000:861). Taking the sign
                               off would let a credit stored the wrong way round read as cover here
                               and nowhere else. See `PolicyShape.otherDeposits`. */
                            onChange={(value, transaction) =>
                                onChange({
                                    depositTransactionId: value,
                                    depositAmount: transaction?.amount ?? null,
                                })
                            }
                            /* THE ONLY DOOR THAT UNLINKS, AND IT CLEARS BOTH. An amount with no id
                               behind it is money that never reaches the database while still telling
                               this screen the premium is covered. */
                            onClear={() =>
                                onChange({ depositTransactionId: '', depositAmount: null })
                            }
                            error={errors?.depositTransactionId}
                            emptyLabel="Nothing paid"
                            hint="the credit the driver paid towards this premium"
                        />
                    </Rows>
                )}

                {/* ONE PRODUCTION LOAN CARRIES TWO PAYMENTS (294: 3,000 + 977 against a premium of
                    3,977), and this dossier binds the lowest-id link. Without this line an admin
                    would read one payment above and a settled premium below it, with nothing on the
                    canvas accounting for the difference. */}
                {otherDeposits > 0 && (
                    <Hint className="mt-3">
                        This loan carries {formatCurrency(otherDeposits)} in other payments from the
                        driver, recorded elsewhere. Every figure below counts them; the link above is
                        the one this page edits.
                    </Hint>
                )}
            </Block>

            <Block
                id="b-recovery"
                title="Weekly recovery"
                aside={
                    isRecord ? 'records, not requirements' : weeklyOptional ? 'optional' : 'required'
                }
            >
                {frozen && <LockLine title={lock.title}>{lock.body}</LockLine>}

                <p className="sd-lede sd-dim">
                    {isRecord
                        ? 'Nothing is advanced on this year, so there is nothing to recover. A weekly figure typed here would put this year back on an Insurance Premium Advance.'
                        : weeklyOptional
                            /* THE ARRANGEMENT, AND NOT WHAT THE BOXES DO TO IT [D-B57]. An advance
                               with no weekly recovery is settled at the end of the contract, and
                               the sentence says what that means for the money: nothing separate is
                               added now, and the instalments paid after the vehicle recover it.
                               Stopping at the label would leave an admin guessing at how the
                               money actually comes back.

                               THE SECOND HALF IS ADDRESSED TO SOMEBODY WHO CAN STILL ACT. A frozen
                               year has no save bar and reads its two boxes, so telling that admin
                               what setting a recovery would do describes a door the page has shut
                               two rows below. What stays is what a reader of a signed year needs:
                               how the advance comes back. */
                            ? `With no weekly recovery, nothing is added to the contract instalment. The advance is settled at the end of the contract: once the vehicle is paid for, the driver keeps paying the same instalment and those payments recover it.${frozen ? '' : ' Set one and the recovery rides on the contract instalment instead — from the Monday of the first recovery’s week the driver owes the raised total, and the Schedule states both figures.'}`
                            : 'The advance is recovered weekly, and the recovery is added to the contract instalment. From the Monday of the first recovery’s week the driver owes the raised total, and the Schedule states both figures.'}
                </p>

                {frozen ? (
                    <Rows>
                        {/* A FROZEN YEAR THAT CARRIES NO RECOVERY IS NOT A YEAR MISSING ONE
                            [D-B57]. "Not set" reads as a blank somebody still has to fill on a page
                            that has already been signed; what the pair says instead is what the
                            signed page says about how the advance comes back.

                            AND EACH BOX ANSWERS ITS OWN LABEL, in the rail's words for the same
                            two rows. The weekly names the arrangement; the start day names the
                            moment recovery begins, which on such a year is the vehicle being paid
                            for and never a date. These reads and `pol-weekly`/`pol-first` are one
                            statement on two surfaces, so they are worded once. */}
                        <Rec
                            label="Weekly recovery"
                            value={money(draft.weeklyAmount)}
                            empty={atEndFiled ? 'Settled at the end of the contract' : 'Not set'}
                        />
                        <Rec
                            label="Recovery starts"
                            value={startsOn ? format(startsOn, 'MMM d, yyyy') : null}
                            empty={atEndFiled ? 'After the vehicle is paid for' : 'Not set'}
                        />
                    </Rows>
                ) : (
                    <Rows>
                        <QuietField
                            id="f-loanInstalment"
                            label="Weekly recovery"
                            value={form.weeklyAmount}
                            onValueChange={(value) => onChange({ weeklyAmount: value })}
                            inputMode="decimal"
                            placeholder="—"
                            format="GHS per week"
                            error={errors?.weeklyAmount}
                        />
                        <QuietField
                            id="f-loanInstalmentStart"
                            label="Recovery starts"
                            type="date"
                            value={form.firstStartsAt}
                            onValueChange={(value) => onChange({ firstStartsAt: value })}
                            error={errors?.firstStartsAt}
                        />
                    </Rows>
                )}

                {/*
                 * THE CLEAR-TYPED-NEVER-SAVED RULE, SAID OUT LOUD ON BOTH SIDES OF IT [D-B45]. A
                 * plan typed in this session is dropped by the edit that makes the driver's payment
                 * cover the premium, because the two describe contradictory years; a recovery
                 * already running against a live contract is never dropped that way, and removing
                 * it stays an explicit act with a line on screen about it.
                 *
                 * AND THE PROMISE THAT CLEARING DELETES IS NOT TRUE EVERYWHERE ANY MORE [D-B57].
                 * `validateDraft` refuses an emptied pair over a saved row on a contract whose hire
                 * purchase agreement leaves room for an advance instalment, so this line states the
                 * refusal's own words there. It is asked of `'NOT_PAPER'` and not of `'PAPER'`'s
                 * negation on purpose: an unread board is not refused by the writer either, so on
                 * one the delete is still what happens and the sentence is still true.
                 */}
                {!frozen && (
                    <Hint className="mt-3">
                        {instalmentSaved
                            ? snapshot.hpClass === 'NOT_PAPER'
                                ? 'A recovery already running is not dropped by linking a payment, and it can be corrected here but not cleared — a premium financed on this contract comes back weekly.'
                                : 'A recovery already running is not dropped by linking a payment. Clearing both boxes deletes the weekly row, and that is the only way it goes.'
                            : 'A weekly plan typed here is cleared the moment the driver’s payment covers the whole premium — there would be nothing left to recover. Both boxes go together: a figure without a start day, or a start day without a figure, is refused.'}
                    </Hint>
                )}
            </Block>

            <Block
                id="b-schedule"
                title={`What the ${INSTRUMENT_TITLES[instrument]} will print`}
                aside="as it will print"
            >
                {/* THE SAME ROWS THE CONFIRM DIALOG DRAWS, FROM THE SAME FUNCTION — the instrument's
                    own order, under the instrument's own labels, as it will print. */}
                <Rows>
                    {summary.clauses.map(clauseRow)}
                    {/* THE TERM IS DERIVED, NEVER STORED, and it amortises the TOTAL REPAYABLE net
                        of the driver's payment [D-B46]. `termWeeks` floors and adds a final week
                        only where the part-week is worth half a pesewa or more [D-T1] — the same
                        guard `agreement_merge_data` applies to `{{contract.term_weeks}}` — and the
                        last recovery is the total MINUS what the full weeks cover, never a
                        percentage or a remainder.

                        AND AN ADVANCE SETTLED AT THE END OF THE CONTRACT HAS NO TERM TO STATE
                        [D-B57]. The recovery does not start on a date: it starts when the vehicle
                        has been paid for, which nobody can count the weeks to while the Schedule is
                        being signed. The row would read "Needs the total repayable and a weekly
                        figure" for ever, which asks for a figure this arrangement does not have. */}
                    {!isRecord && !atEndDraft && (
                        <Rec
                            label="Expected term"
                            value={term.term === null ? null : `${term.term} weeks`}
                            sub={
                                term.finalInstalment === null
                                    ? undefined
                                    : `final recovery ${formatCurrency(term.finalInstalment)}`
                            }
                            empty="Needs the total repayable and a weekly figure"
                        />
                    )}
                </Rows>
            </Block>

            {/* THE SAME PANEL THE ADVANCE DOSSIER DRAWS, AND THE LABELS ARE WHAT DIFFER. This
                canvas's `forward` has THIS policy's loan taken out of the insurance sum, so the row
                that reads "Insurance premium advances" over there has to say "other" here — it is
                naming a sum that excludes the very loan the admin is looking at. No term row: this
                dossier's term is net of the driver's payment and stands on the block above.

                AND IT IS NOT DRAWN AT ALL ON A PAYMENT RECORD YEAR [D-B45]. The panel answers "what
                will the driver owe every Monday once this is saved?", and this canvas's whole
                contribution to that sum is the weekly recovery — which a Record does not have, by
                definition: nothing was advanced, so nothing is recovered. What would be left is a
                block of advance nouns stating a total this page is about to move by nought. */}
            {!isRecord && (
                <ForwardWeeklyPanel
                    forward={forward}
                    typed={draft.weeklyAmount}
                    insuranceLoansLabel="Other insurance premium advances"
                    loansLabel="Operational expense advances"
                />
            )}

            {mode === 'create' && (
                <Hint>
                    Creating the policy writes the cover, the premium payment and this advance in one
                    sequence. If the policy or the junction fails, neither is left behind; anything
                    after them that does not land shows as an open row on the journey.
                </Hint>
            )}

            {commit}
        </Blocks>
    );
}
